import { createServerFn } from "@tanstack/react-start";
import { createClient } from "@supabase/supabase-js";
import { getRequest } from "@tanstack/react-start/server";
import { createHash } from "crypto";
import { z } from "zod";
import { validateEmail } from "@/lib/email-validation";
import type { Database } from "@/integrations/supabase/types";

const subscribeSchema = z.object({
  email: z
    .string()
    .trim()
    .toLowerCase()
    .max(254)
    .superRefine((value, ctx) => {
      const check = validateEmail(value);
      if (!check.ok) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: check.error ?? "Enter a valid email address.",
        });
      }
    }),
  source: z.string().trim().min(1).max(50).default("home"),
});

const confirmSchema = z.object({
  token: z.string().uuid(),
});

function serverClient() {
  const key = process.env.SUPABASE_PUBLISHABLE_KEY!;
  const url = process.env.SUPABASE_URL!;
  const isNew = key.startsWith("sb_publishable_") || key.startsWith("sb_secret_");
  return createClient<Database>(url, key, {
    auth: { persistSession: false, autoRefreshToken: false, storage: undefined },
    global: {
      fetch: (input, init) => {
        const h = new Headers(init?.headers);
        if (isNew && h.get("Authorization") === `Bearer ${key}`) h.delete("Authorization");
        h.set("apikey", key);
        return fetch(input, { ...init, headers: h });
      },
    },
  });
}

function hashIp(): string | null {
  try {
    const req = getRequest();
    const ip =
      req?.headers.get("x-forwarded-for")?.split(",")[0]?.trim() ||
      req?.headers.get("cf-connecting-ip") ||
      req?.headers.get("x-real-ip") ||
      "";
    if (!ip) return null;
    return createHash("sha256").update(ip + "|buddhaka-newsletter").digest("hex").slice(0, 32);
  } catch {
    return null;
  }
}

export const subscribeEmail = createServerFn({ method: "POST" })
  .inputValidator((data: unknown) => subscribeSchema.parse(data))
  .handler(async ({ data }) => {
    const supabase = serverClient();
    const ip_hash = hashIp();

    if (ip_hash) {
      const { checkRateLimit } = await import("@/lib/rate-limit.server");
      const allowed = await checkRateLimit(`newsletter:${ip_hash}`, 5, 3600);
      if (!allowed) throw new Error("Too many submissions. Please try again later.");
    }


    // Idempotent: if email already exists, return its current status.
    const { data: existing } = await supabase
      .from("email_subscribers")
      .select("id, status")
      .eq("email", data.email)
      .maybeSingle();

    if (existing) {
      return { ok: true, status: existing.status, duplicate: true as const };
    }

    const { error } = await supabase
      .from("email_subscribers")
      .insert({ email: data.email, source: data.source, ip_hash });

    if (error) {
      // Race condition — another insert won. Treat as duplicate success.
      if (error.code === "23505") {
        return { ok: true, status: "pending", duplicate: true as const };
      }
      throw new Error("Unable to subscribe. Please try again.");
    }

    return { ok: true, status: "pending", duplicate: false as const };
  });

export const confirmSubscription = createServerFn({ method: "POST" })
  .inputValidator((data: unknown) => confirmSchema.parse(data))
  .handler(async ({ data }) => {
    const supabase = serverClient();
    // Use service role only for this narrow confirm-by-token op.
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { data: row, error } = await supabaseAdmin
      .from("email_subscribers")
      .update({ status: "confirmed", confirmed_at: new Date().toISOString() })
      .eq("confirmation_token", data.token)
      .in("status", ["pending", "confirmed"])
      .select("id, email, status")
      .maybeSingle();
    if (error) throw new Error("Confirmation failed.");
    if (!row) return { ok: false as const };
    void supabase; // reserved
    return { ok: true as const, status: row.status };
  });
